import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../../App';
import { coverUrl, COVER_WIDTH } from '../../lib/coverUrl';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Send } from 'lucide-react';
import ReturnBookToWriterDialog from '../../components/ReturnBookToWriterDialog';

const AvailableBooks = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);
  const [returnBook, setReturnBook] = useState(null);

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API}/proofreading/available-books`);
      setBooks(response.data);
    } catch (error) {
      toast.error('Kon nie boeke laai nie');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBook = async (bookId) => {
    setSelecting(bookId);
    try {
      await axios.post(`${API}/proofreading/select-book/${bookId}`);
      toast.success('Boek suksesvol gekies!');
      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie boek kies nie');
    } finally {
      setSelecting(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Beskikbare Boeke</h2>
        <p className="text-gray-600">Kies &rsquo;n boek om te proeflees</p>
      </div>

      {books.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><p className="text-gray-500">Geen beskikbare boeke nie</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {books.map((book) => (
            <Card key={book.id}>
              <CardContent className="p-6">
                <div className="flex gap-6">
                  <img src={coverUrl(book.id, COVER_WIDTH.list)} alt={book.boek_naam} loading="lazy" width={96} height={144} className="w-24 h-36 object-cover rounded shadow" />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{book.boek_naam}</h3>
                    <p className="text-gray-600 mb-4">deur {book.skrywer_naam}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                      <div><p className="text-gray-500">Genre</p><p className="font-semibold">{book.genre}</p></div>
                      <div><p className="text-gray-500">Woorde</p><p className="font-semibold">{book.word_count.toLocaleString()}</p></div>
                      <div><p className="text-gray-500">Bladsye</p><p className="font-semibold">{book.page_count}</p></div>
                      <div><p className="text-gray-500">Betaling</p><p className="font-semibold text-green-600">R2 per fout</p></div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        data-testid={`select-book-btn-${book.id}`}
                        onClick={() => handleSelectBook(book.id)}
                        disabled={selecting === book.id}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        {selecting === book.id ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Besig...</> : 'Kies vir Proeflees'}
                      </Button>
                      <Button
                        data-testid={`return-book-btn-${book.id}`}
                        onClick={() => setReturnBook(book)}
                        variant="outline"
                        className="border-amber-500 text-amber-700 hover:bg-amber-50"
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Stuur Terug na Skrywer
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ReturnBookToWriterDialog
        open={!!returnBook}
        book={returnBook}
        onClose={() => setReturnBook(null)}
        onSent={() => fetchBooks()}
      />
    </div>
  );
};

export default AvailableBooks;
